/**
 * BIES HOUSE - Modern Cart Drawer
 * Handles all cart drawer functionality
 */

// Debug utility — only logs when window.BIES_DEBUG is truthy
function biesDebug(...args) {
  if (window.BIES_DEBUG) console.log(...args);
}

// Prevent double initialization
if (window.biesCartDrawerInitialized) {
  biesDebug('[BIES Cart Drawer] Already initialized, skipping duplicate');
} else {
  window.biesCartDrawerInitialized = true;
  biesDebug('[BIES Cart Drawer] Script loaded');
}

class BiesCartDrawer {
  constructor() {
    this.drawer = null;
    this.overlay = null;
    this.REWARDS_TIERS = [
      { threshold: 4000, giftName: 'Granola Original', shortName: 'Granola', giftVariantId: 52456154431835, realValue: 590 },
      { threshold: 5000, giftName: 'Stevia BIES', shortName: 'BIES', giftVariantId: 52456161771867, realValue: 1450 },
      { threshold: 7000, giftName: 'Batido de Proteínas', shortName: 'Batido', giftVariantId: 52456162066779, realValue: 1950 }
    ];
    this.REWARDS_MAX = 7000;
    this.GIFT_VARIANT_IDS = new Set([52456154431835, 52456161771867, 52456162066779]);
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    biesDebug('[BIES Cart Drawer] Setup starting...');
    this.drawer = document.getElementById('bies-cart-drawer');
    this.overlay = document.getElementById('bies-cart-drawer-overlay');

    if (!this.drawer) {
      console.error('[BIES Cart Drawer] CRITICAL: Drawer element #bies-cart-drawer not found!');
      return;
    }

    biesDebug('[BIES Cart Drawer] Drawer element found, binding events...');
    this.bindEvents();

    // Defer cart pipeline to idle time — drawer is hidden, no need to block page load
    this._cartReady = false;
    this._lastCartUpdate = 0;
    const runUpdate = () => {
      this.updateCart().then(() => { this._cartReady = true; });
    };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(runUpdate, { timeout: 2000 });
    } else {
      setTimeout(runUpdate, 100);
    }

    biesDebug('[BIES Cart Drawer] Setup complete');
  }

  bindEvents() {
    biesDebug('[BIES Cart Drawer] bindEvents() called');

    // Close buttons
    const closeBtns = this.drawer.querySelectorAll('[data-cart-drawer-close]');
    closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Store reference to this for event handlers
    const self = this;

    // Unified click handler for all cart drawer interactions
    biesDebug('[BIES Cart Drawer] Adding click handler to drawer');
    this.drawer.addEventListener('click', function(e) {
      biesDebug('[BIES Cart Drawer] Click detected on:', e.target.tagName, e.target.className);
      // Handle quantity buttons
      const qtyBtn = e.target.closest('[data-qty-action]');
      if (qtyBtn) {
        e.preventDefault();
        e.stopPropagation();
        const action = qtyBtn.getAttribute('data-qty-action');
        const lineIndex = qtyBtn.getAttribute('data-line-index');
        biesDebug('[BIES Cart Drawer] Qty button clicked:', action, 'line:', lineIndex);
        self.updateQuantity(lineIndex, action);
        return;
      }

      // Handle remove item buttons
      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        const lineIndex = removeBtn.getAttribute('data-line-index');
        biesDebug('[BIES Cart Drawer] Remove button clicked for line:', lineIndex);
        self.removeItem(lineIndex);
        return;
      }

      // Handle add recommended product buttons
      const addBtn = e.target.closest('[data-add-recommended]');
      if (addBtn) {
        e.preventDefault();
        e.stopPropagation();
        const variantId = addBtn.getAttribute('data-variant-id');
        biesDebug('[BIES Cart Drawer] Add recommended clicked:', variantId);
        self.addRecommendedProduct(variantId, addBtn);
        return;
      }
    });

    // Listen for cart updates from other sources
    document.addEventListener('cart:updated', () => {
      this.updateCart();
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  open(options = {}) {
    if (!this.drawer) return;

    this.drawer.classList.add('is-open');
    if (this.overlay) {
      this.overlay.classList.add('is-visible');
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Lazy-render payment icons (~40KB of inline SVG) on first open only.
    // Defensive: never let this break drawer opening if markup is missing.
    this._renderPaymentIcons();

    // Update cart data when opening (skip if caller already did it, or if recently updated)
    if (!options.skipUpdate) {
      const cacheAge = Date.now() - (this._lastCartUpdate || 0);
      if (!this._cartReady || cacheAge > 30000) {
        this.updateCart().then(() => { this._cartReady = true; });
      }
    }

    // Analytics
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'cart_drawer_opened'
      });
    }
  }

  /**
   * Clones the payment-icon SVGs from their <template> into the drawer
   * the first time it opens. Idempotent (no-ops if already rendered).
   * Defensive: swallows errors so a markup change can never block the
   * drawer from opening.
   */
  _renderPaymentIcons() {
    if (this._paymentIconsRendered) return;
    try {
      const target = document.getElementById('bies-cart-drawer-payment-icons');
      const tpl = document.getElementById('bies-cart-drawer-payment-icons-template');
      if (!target || !tpl || !tpl.content) return;
      target.appendChild(tpl.content.cloneNode(true));
      this._paymentIconsRendered = true;
    } catch (error) {
      biesDebug('[BIES Cart Drawer] Payment icons lazy-render failed (non-fatal):', error);
    }
  }

  close() {
    if (!this.drawer) return;

    this.drawer.classList.remove('is-open');
    if (this.overlay) {
      this.overlay.classList.remove('is-visible');
    }

    // Restore body scroll
    document.body.style.overflow = '';

    // Analytics
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'cart_drawer_closed'
      });
    }
  }

  isOpen() {
    return this.drawer && this.drawer.classList.contains('is-open');
  }

  async updateCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      await this.processCart(cart);
      this._lastCartUpdate = Date.now();
    } catch (error) {
      console.error('[BIES Cart Drawer] Error updating cart:', error);
      this._normalizing = false;
      this._syncingGifts = false;
    }
  }

  /**
   * Process cart data and render. Accepts cart object directly (avoids re-fetching /cart.js).
   * @param {Object} cart - Full cart object (from /cart.js or /cart/change.js response)
   * @param {Object} options
   * @param {boolean} options.skipNormalization - Skip bundle normalization (for simple qty changes on non-bundle items)
   */
  async processCart(cart, { skipNormalization = false } = {}) {
    try {
      // Auto-normalize bundle quantities (duplicates or qty > 1 → upgrade tier)
      if (!skipNormalization && !this._normalizing) {
        this._normalizing = true;
        let normalized = true;
        let maxAttempts = 5;
        while (normalized && maxAttempts > 0) {
          normalized = await this.normalizeBundleQuantities(cart);
          if (normalized) {
            this.setLoading(true);
            const refreshResponse = await fetch('/cart.js');
            cart = await refreshResponse.json();
            this.setLoading(false);
          }
          maxAttempts--;
        }
        this._normalizing = false;
      }

      // RENDER FIRST — user sees updated cart immediately
      this.renderCart(cart);
      this.updateRewardsBar(cart.total_price);
      document.dispatchEvent(new CustomEvent('bies:cart:updated', { detail: cart }));

      // THEN sync gifts in background (non-blocking for UI)
      if (!this._syncingGifts) {
        this._syncingGifts = true;
        this.syncGiftProducts(cart).then(async (giftChanged) => {
          if (giftChanged) {
            const refreshResponse = await fetch('/cart.js');
            const updatedCart = await refreshResponse.json();
            this.renderCart(updatedCart);
            this.updateRewardsBar(updatedCart.total_price);
          }
        }).catch(err => {
          console.error('[BIES Gifts] Error in gift sync:', err);
        }).finally(() => {
          this._syncingGifts = false;
        });
      }

      // Update compare_at_prices in background (for discount badges)
      this.updateCompareAtPrices(cart.items).then(() => {
        // Re-render only if we got new prices
        this.renderCart(cart);
      }).catch(() => {});

    } catch (error) {
      console.error('[BIES Cart Drawer] Error processing cart:', error);
      this._normalizing = false;
      this._syncingGifts = false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Auto-normalize bundle items in cart.
   * Detects two scenarios:
   *   1. Same variant added multiple times (qty > 1)
   *   2. Multiple tiers of the same bundle in cart (e.g. tier 1 + tier 2 = 3 units → tier 3)
   * Consolidates into the correct single tier with qty=1, giving the customer the best discount.
   */
  async normalizeBundleQuantities(cart) {
    const bundleMap = window.BIES_VARIANT_TO_BUNDLE || {};
    const bundleVariants = window.BIES_BUNDLE_VARIANTS || {};
    const maxTier = window.BIES_MAX_TIER || 4;
    const tierDiscounts = window.BIES_TIER_DISCOUNTS || {};

    // Group cart items by bundleCode
    const bundleGroups = {};
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      const bundleInfo = bundleMap[String(item.variant_id)];
      if (bundleInfo) {
        const code = bundleInfo.bundleCode;
        if (!bundleGroups[code]) bundleGroups[code] = [];
        bundleGroups[code].push({
          tier: bundleInfo.tier,
          quantity: item.quantity,
          variantId: String(item.variant_id)
        });
      }
    }

    // Check each bundle group
    for (const code of Object.keys(bundleGroups)) {
      const group = bundleGroups[code];

      // Calculate total units across all items of this bundle
      let totalUnits = 0;
      for (const entry of group) {
        totalUnits += entry.tier * entry.quantity;
      }

      // Needs normalization if: multiple line items OR any item with qty > 1
      const needsNormalization = group.length > 1 || group[0].quantity > 1;
      if (!needsNormalization) continue;

      const targetTier = Math.min(totalUnits, maxTier);
      const tiers = bundleVariants[code];
      if (!tiers || !tiers[targetTier]) continue;

      biesDebug('[BIES Cart Drawer] Auto-normalizing:', code,
        'items=' + group.length, 'totalUnits=' + totalUnits, '→ tier=' + targetTier);

      // Remove all variants of this bundle at once using /cart/update.js
      const updates = {};
      for (const entry of group) {
        updates[entry.variantId] = 0;
      }
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });

      // Add the correct tier at qty=1
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: tiers[targetTier], quantity: 1 }] })
      });

      const discount = tierDiscounts[targetTier] || 0;
      if (discount > 0) {
        this.showNotification('¡Mejorado a pack ' + targetTier + 'x (-' + discount + '%)!');
      }

      return true; // Re-fetch cart before processing other bundles
    }

    return false;
  }

  async updateCompareAtPrices(items) {
    // Initialize cache if it doesn't exist
    if (!window.biesCompareAtPrices) {
      window.biesCompareAtPrices = {};
    }

    // Collect items that need fetching (not yet cached)
    const uncachedItems = items.filter(item => window.biesCompareAtPrices[item.variant_id] === undefined);
    if (uncachedItems.length === 0) return;

    // Deduplicate by handle (one product fetch covers all its variants)
    const handlesSeen = new Set();
    const uniqueItems = uncachedItems.filter(item => {
      if (handlesSeen.has(item.handle)) return false;
      handlesSeen.add(item.handle);
      return true;
    });

    // Fetch all products in parallel
    const fetches = uniqueItems.map(async (item) => {
      try {
        const productResponse = await fetch(`/products/${item.handle}.js`);
        const productData = await productResponse.json();

        // Cache all variants from this product at once
        for (const variant of productData.variants) {
          if (window.biesCompareAtPrices[variant.id] === undefined) {
            window.biesCompareAtPrices[variant.id] = variant.compare_at_price || 0;
          }
        }
        biesDebug('[BIES Cart Drawer] Cached compare_at_prices for', item.handle);
      } catch (error) {
        biesDebug('[BIES Cart Drawer] Could not fetch compare_at_price for', item.handle, error);
        // Mark uncached variants for this item so we don't retry endlessly
        for (const uncached of uncachedItems.filter(i => i.handle === item.handle)) {
          window.biesCompareAtPrices[uncached.variant_id] = 0;
        }
      }
    });

    await Promise.all(fetches);
  }

  async syncGiftProducts(cart) {
    // Frame: cart object from /cart.js, GIFT_VARIANT_IDS populated, REWARDS_TIERS configured
    biesDebug('[BIES Gifts] syncGiftProducts called — cart total:', cart.total_price, 'cents, items:', cart.item_count);

    // Step 1: Identify which gift variants are currently in cart
    const currentGiftIds = new Set();
    for (const item of cart.items) {
      if (this.GIFT_VARIANT_IDS.has(item.variant_id)) {
        currentGiftIds.add(item.variant_id);
      }
    }
    biesDebug('[BIES Gifts] Current gifts in cart:', [...currentGiftIds]);

    // Step 2: Calculate which gifts SHOULD be in cart based on thresholds
    const total = cart.total_price;
    const shouldHave = new Set();
    for (const tier of this.REWARDS_TIERS) {
      if (total >= tier.threshold) {
        shouldHave.add(tier.giftVariantId);
        biesDebug('[BIES Gifts] Threshold met:', tier.giftName, '(' + tier.threshold + ' cents)');
      }
    }

    const toAdd = [...shouldHave].filter(id => !currentGiftIds.has(id));
    const toRemove = [...currentGiftIds].filter(id => !shouldHave.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      biesDebug('[BIES Gifts] No changes needed');
      return false;
    }

    biesDebug('[BIES Gifts] To add:', toAdd, '| To remove:', toRemove);
    let changed = false;

    // Step 3: Remove gifts that shouldn't be there
    if (toRemove.length > 0) {
      try {
        const updates = {};
        for (const variantId of toRemove) {
          updates[String(variantId)] = 0;
        }
        const removeResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        if (!removeResponse.ok) {
          const errorBody = await removeResponse.text();
          console.error('[BIES Gifts] Remove failed:', removeResponse.status, errorBody);
        } else {
          biesDebug('[BIES Gifts] Removed', toRemove.length, 'gift(s)');
          changed = true;
        }
      } catch (err) {
        console.error('[BIES Gifts] Remove error:', err);
      }
    }

    // Step 4: Add gifts that should be there (one by one for better error visibility)
    for (const variantId of toAdd) {
      const tierInfo = this.REWARDS_TIERS.find(t => t.giftVariantId === variantId);
      const giftName = tierInfo ? tierInfo.giftName : 'Unknown';
      biesDebug('[BIES Gifts] Adding gift:', giftName, '(variant:', variantId, ')');

      try {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: String(variantId),
              quantity: 1,
              properties: { '_gift': 'true' }
            }]
          })
        });

        if (!addResponse.ok) {
          const errorBody = await addResponse.text();
          console.error('[BIES Gifts] ADD FAILED for', giftName, '— Status:', addResponse.status, '— Body:', errorBody);

          // Retry once after short delay (Shopify propagation)
          biesDebug('[BIES Gifts] Retrying', giftName, 'in 500ms...');
          await new Promise(r => setTimeout(r, 500));
          const retryResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: String(variantId),
                quantity: 1,
                properties: { '_gift': 'true' }
              }]
            })
          });
          if (!retryResponse.ok) {
            const retryBody = await retryResponse.text();
            console.error('[BIES Gifts] RETRY ALSO FAILED for', giftName, '—', retryResponse.status, retryBody);
          } else {
            biesDebug('[BIES Gifts] Retry SUCCESS for', giftName);
            changed = true;
          }
        } else {
          const addData = await addResponse.json();
          biesDebug('[BIES Gifts] Added', giftName, '— Response:', addData.items ? addData.items.length + ' item(s)' : 'ok');
          changed = true;
        }
      } catch (err) {
        console.error('[BIES Gifts] Network error adding', giftName, ':', err);
      }
    }

    biesDebug('[BIES Gifts] Sync complete — changed:', changed);
    return changed;
  }

  renderCart(cart) {
    biesDebug('[BIES Cart Drawer] renderCart called with', cart.item_count, 'items');

    const itemsContainer = document.getElementById('bies-cart-items');
    const emptyState = document.getElementById('bies-cart-empty');
    const bodyContent = document.getElementById('bies-cart-body-content');
    const subtotalValue = document.getElementById('bies-cart-subtotal-value');
    const checkoutBtn = document.getElementById('bies-cart-checkout-btn');

    biesDebug('[BIES Cart Drawer] DOM elements found:', {
      itemsContainer: !!itemsContainer,
      emptyState: !!emptyState,
      bodyContent: !!bodyContent,
      subtotalValue: !!subtotalValue,
      checkoutBtn: !!checkoutBtn
    });

    if (!itemsContainer) {
      console.error('[BIES Cart Drawer] itemsContainer NOT FOUND! Cannot render cart.');
      return;
    }

    // Update subtotal
    if (subtotalValue) {
      subtotalValue.textContent = this.formatMoney(cart.total_price);
    }

    // Update checkout button state (price removed per user request - redundant with total above)
    // Checkout button now only shows "FINALIZAR COMPRA" without price

    // Show/hide empty state
    if (cart.item_count === 0) {
      if (emptyState) emptyState.style.display = 'flex';
      if (bodyContent) bodyContent.style.display = 'none';
      if (checkoutBtn) checkoutBtn.disabled = true;
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    if (bodyContent) bodyContent.style.display = 'flex';
    if (checkoutBtn) checkoutBtn.disabled = false;

    // Render items
    const newHTML = cart.items.map((item, index) => {
      return this.renderCartItem(item, index + 1);
    }).join('');

    biesDebug('[BIES Cart Drawer] Setting innerHTML with', cart.items.length, 'items');
    itemsContainer.innerHTML = newHTML;
    biesDebug('[BIES Cart Drawer] innerHTML updated. Container now has', itemsContainer.children.length, 'children');

    // Update recommended products (filter out items already in cart)
    this.updateRecommendedProducts(cart);
  }

  renderCartItem(item, lineIndex) {
    // Gift products are hidden from the item list — shown only via rewards bar + summary badge
    if (this.GIFT_VARIANT_IDS.has(item.variant_id)) {
      return '';
    }

    const imageUrl = item.image ? item.image : '';
    const variantTitle = item.variant_title !== 'Default Title' ? item.variant_title : '';
    const finalLinePrice = item.final_line_price;

    // Detect bundle via shared map
    const bundleMap = window.BIES_VARIANT_TO_BUNDLE || {};
    const tierDiscounts = window.BIES_TIER_DISCOUNTS || { 1: 0, 2: 10, 3: 15, 4: 20 };
    const maxTier = window.BIES_MAX_TIER || 4;
    const bundleInfo = bundleMap[String(item.variant_id)];

    let discountPercent = 0;
    let displayQty = item.quantity;
    let isBundle = false;
    let currentTier = 0;
    let isPlusDisabled = false;

    if (bundleInfo) {
      isBundle = true;
      currentTier = bundleInfo.tier;
      discountPercent = tierDiscounts[currentTier] || 0;
      displayQty = currentTier; // Show tier as visual quantity
      isPlusDisabled = currentTier >= maxTier;
    } else {
      // Fallback: detect discount from title for non-mapped bundles
      const title = item.product_title || '';
      if (title.match(/^4x\s/i)) {
        discountPercent = 20;
      } else if (title.match(/^3x\s/i)) {
        discountPercent = 15;
      } else if (title.match(/^2x\s/i)) {
        discountPercent = 10;
      }
    }

    const hasDiscount = discountPercent > 0;

    biesDebug('[BIES Cart Drawer] Item:', item.product_title, '- Qty:', item.quantity,
      '- Bundle:', isBundle, '- Tier:', currentTier, '- Discount:', discountPercent + '%');

    const tierLabel = isBundle && currentTier > 1
      ? `<span class="bies-cart-item__tier-label">${currentTier}x unidades · -${discountPercent}%</span>`
      : (isBundle ? `<span class="bies-cart-item__tier-label">1 unidad</span>` : '');

    return `
      <div class="bies-cart-item" data-line-index="${lineIndex}" data-variant-id="${item.variant_id}">
        ${imageUrl ? `
          <img
            src="${imageUrl}"
            alt="${this.escapeHtml(item.product_title)}"
            class="bies-cart-item__image"
            loading="lazy"
          >
        ` : ''}

        <div class="bies-cart-item__details">
          <h3 class="bies-cart-item__title">${this.escapeHtml(item.product_title)}</h3>
          ${variantTitle ? `
            <p class="bies-cart-item__variant">${this.escapeHtml(variantTitle)}</p>
          ` : ''}

          <div class="bies-cart-item__footer">
            <div class="bies-cart-item__quantity">
              <button
                class="bies-cart-item__qty-btn"
                data-qty-action="minus"
                data-line-index="${lineIndex}"
                aria-label="Disminuir cantidad"
              >
                −
              </button>
              <span class="bies-cart-item__qty-value">${displayQty}</span>
              <button
                class="bies-cart-item__qty-btn${isPlusDisabled ? ' is-disabled' : ''}"
                data-qty-action="plus"
                data-line-index="${lineIndex}"
                aria-label="Aumentar cantidad"
                ${isPlusDisabled ? 'disabled' : ''}
              >
                +
              </button>
            </div>
            ${tierLabel}

            <button
              class="bies-cart-item__remove"
              data-remove-item
              data-line-index="${lineIndex}"
              aria-label="Eliminar producto"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </div>
        </div>

        <div class="bies-cart-item__price">
          ${hasDiscount ? `
            <span class="bies-cart-item__price-badge">-${discountPercent}%</span>
          ` : ''}
          <span class="bies-cart-item__price-current">${this.formatMoney(finalLinePrice)}</span>
        </div>
      </div>
    `;
  }

  renderGiftItem(item) {
    const comparePrice = window.biesCompareAtPrices?.[item.variant_id] || 0;
    const giftTitle = item.product_title.replace('REGALO: ', '');

    return `
      <div class="bies-cart-item bies-cart-item--gift-compact">
        <svg class="bies-cart-item__gift-icon-sm" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6DB284" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/>
        </svg>
        <span class="bies-cart-item__gift-name">${this.escapeHtml(giftTitle)}</span>
        <span class="bies-cart-item__gift-tag">GRATIS</span>
        ${comparePrice > 0 ? `<span class="bies-cart-item__gift-value">(val. ${this.formatMoney(comparePrice)})</span>` : ''}
      </div>
    `;
  }

  async updateQuantity(lineIndex, action) {
    const item = document.querySelector(`[data-line-index="${lineIndex}"]`);
    if (!item) return;

    // Check if this is a bundle product
    const variantId = item.getAttribute('data-variant-id');
    const bundleMap = window.BIES_VARIANT_TO_BUNDLE || {};
    const bundleInfo = variantId ? bundleMap[String(variantId)] : null;

    if (bundleInfo) {
      // Bundle product → swap tiers instead of changing quantity
      await this.swapBundleTier(lineIndex, variantId, bundleInfo, action);
      return;
    }

    // Non-bundle product → normal quantity change
    const qtyElement = item.querySelector('.bies-cart-item__qty-value');
    if (!qtyElement) return;

    let currentQty = parseInt(qtyElement.textContent);
    let newQty = action === 'plus' ? currentQty + 1 : currentQty - 1;

    // Don't allow quantity less than 0
    if (newQty < 0) return;

    // If quantity is 0, remove the item
    if (newQty === 0) {
      this.removeItem(lineIndex);
      return;
    }

    // Optimistic UI: update qty, line price, and subtotal instantly
    qtyElement.textContent = newQty;

    const priceElement = item.querySelector('.bies-cart-item__price-current');
    const subtotalElement = document.getElementById('bies-cart-subtotal-value');
    let oldLinePrice = null;
    let unitPrice = null;

    if (priceElement) {
      oldLinePrice = this.parseMoney(priceElement.textContent);
      unitPrice = Math.round(oldLinePrice / currentQty);
      priceElement.textContent = this.formatMoney(unitPrice * newQty);
    }
    if (subtotalElement && unitPrice !== null) {
      const oldSubtotal = this.parseMoney(subtotalElement.textContent);
      const newSubtotal = oldSubtotal + unitPrice * (newQty - currentQty);
      subtotalElement.textContent = this.formatMoney(newSubtotal);
      // Update rewards bar optimistically
      this.updateRewardsBar(newSubtotal);
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex,
          quantity: newQty
        })
      });

      if (!response.ok) throw new Error('Failed to update quantity');

      // Use response directly (change.js returns full cart) — skip normalization for non-bundle items
      const cart = await response.json();
      await this.processCart(cart, { skipNormalization: true });

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'cart_quantity_updated',
          quantity: newQty,
          line_index: lineIndex
        });
      }

    } catch (error) {
      console.error('[BIES Cart Drawer] Error updating quantity:', error);
      // Revert optimistic UI on error
      qtyElement.textContent = currentQty;
      if (priceElement && oldLinePrice !== null) priceElement.textContent = this.formatMoney(oldLinePrice);
      if (subtotalElement) {
        const revertSubtotal = this.parseMoney(subtotalElement.textContent) - unitPrice * (newQty - currentQty);
        subtotalElement.textContent = this.formatMoney(revertSubtotal);
        this.updateRewardsBar(revertSubtotal);
      }
      this.showNotification('Error al actualizar cantidad', 'error');
    }
  }

  /**
   * Swap between bundle tiers (remove current tier, add new tier)
   */
  async swapBundleTier(lineIndex, currentVariantId, bundleInfo, action) {
    const maxTier = window.BIES_MAX_TIER || 4;
    const minTier = window.BIES_MIN_TIER || 1;
    const bundleVariants = window.BIES_BUNDLE_VARIANTS || {};
    const tierDiscounts = window.BIES_TIER_DISCOUNTS || {};

    const currentTier = bundleInfo.tier;
    const bundleCode = bundleInfo.bundleCode;
    const newTier = action === 'plus' ? currentTier + 1 : currentTier - 1;

    biesDebug('[BIES Cart Drawer] Bundle swap:', bundleCode, 'tier', currentTier, '→', newTier);

    // At max tier and pressing +, do nothing
    if (newTier > maxTier) {
      biesDebug('[BIES Cart Drawer] Already at max tier');
      return;
    }

    // Below min tier, remove item from cart
    if (newTier < minTier) {
      biesDebug('[BIES Cart Drawer] Below min tier, removing item');
      this.removeItem(lineIndex);
      return;
    }

    // Get new variant ID
    const tiers = bundleVariants[bundleCode];
    if (!tiers || !tiers[newTier]) {
      console.error('[BIES Cart Drawer] No variant found for', bundleCode, 'tier', newTier);
      this.showNotification('Error: tier no disponible', 'error');
      return;
    }

    const newVariantId = tiers[newTier];

    try {
      this.setLoading(true);

      // Step 1: Remove current item (set quantity to 0)
      const removeResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: parseInt(lineIndex, 10),
          quantity: 0
        })
      });

      if (!removeResponse.ok) {
        throw new Error('Failed to remove current tier');
      }

      // Step 2: Add new tier variant (quantity 1)
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: newVariantId,
            quantity: 1
          }]
        })
      });

      if (!addResponse.ok) {
        // Rollback: re-add the original variant
        console.error('[BIES Cart Drawer] Add failed, rolling back...');
        await this.rollbackBundleSwap(currentVariantId);
        this.showNotification('Error al cambiar pack, restaurado original', 'error');
        await this.updateCart();
        return;
      }

      // Step 3: Refresh cart — add.js doesn't return full cart, so fetch + process
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      await this.processCart(cart);

      // Notification
      const discount = tierDiscounts[newTier] || 0;
      if (discount > 0) {
        this.showNotification(`Cambiado a pack ${newTier}x (-${discount}%)`);
      } else {
        this.showNotification('Cambiado a 1 unidad');
      }

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'bundle_tier_swapped',
          bundle_code: bundleCode,
          from_tier: currentTier,
          to_tier: newTier,
          discount_percent: discount
        });
      }

      // Trigger free shipping widget update (cart already fetched above)
      if (window.updateFreeShippingBar) {
        window.updateFreeShippingBar(cart.total_price);
      }

    } catch (error) {
      console.error('[BIES Cart Drawer] Error swapping bundle tier:', error);
      this.showNotification('Error al cambiar cantidad', 'error');
      await this.updateCart();
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Rollback: re-add the original variant if add of new tier fails
   */
  async rollbackBundleSwap(originalVariantId) {
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: originalVariantId,
            quantity: 1
          }]
        })
      });
      biesDebug('[BIES Cart Drawer] Rollback successful, re-added variant:', originalVariantId);
    } catch (rollbackError) {
      console.error('[BIES Cart Drawer] Rollback FAILED:', rollbackError);
    }
  }

  async removeItem(lineIndex) {
    biesDebug('[BIES Cart Drawer] Removing item from line:', lineIndex);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      // Use response directly (change.js returns full cart) — skip normalization
      const cart = await response.json();
      await this.processCart(cart, { skipNormalization: true });

      this.showNotification('Producto eliminado del carrito');

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'cart_item_removed',
          line_index: lineIndex
        });
      }

    } catch (error) {
      console.error('[BIES Cart Drawer] Error removing item:', error);
      this.showNotification('Error al eliminar producto', 'error');
    }
  }

  async addRecommendedProduct(variantId, button) {
    if (!variantId) return;

    const originalText = button.innerHTML;
    button.disabled = true;
    button.innerHTML = 'Añadiendo...';

    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        })
      });

      if (!addResponse.ok) throw new Error('Failed to add product');

      // add.js doesn't return full cart, so fetch it then process
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      await this.processCart(cart);

      this.showNotification('¡Producto añadido!');

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'cart_recommended_added',
          variant_id: variantId
        });
      }

    } catch (error) {
      console.error('[BIES Cart Drawer] Error adding recommended product:', error);
      this.showNotification('Error al añadir producto', 'error');
      button.innerHTML = originalText;
      button.disabled = false;
    }
  }

  updateRewardsBar(totalPrice) {
    const rewardsSection = document.getElementById('bies-cart-rewards');
    const progressFill = document.getElementById('bies-rewards-fill');
    const rewardsText = document.getElementById('bies-rewards-text');
    // giftsContainer removed — progress bar milestones are self-explanatory

    if (!rewardsSection || !progressFill) {
      biesDebug('[BIES Rewards] Missing DOM elements:', { rewardsSection: !!rewardsSection, progressFill: !!progressFill });
      return;
    }

    // Update fill bar
    const percentage = Math.min((totalPrice / this.REWARDS_MAX) * 100, 100);
    biesDebug('[BIES Rewards] Bar update:', { totalPrice, max: this.REWARDS_MAX, percentage: percentage.toFixed(1) + '%' });
    progressFill.style.width = `${percentage}%`;

    // Determine unlocked tiers and update milestones
    let unlockedCount = 0;
    let nextTier = null;

    for (let i = 0; i < this.REWARDS_TIERS.length; i++) {
      const tier = this.REWARDS_TIERS[i];
      const milestone = document.getElementById(`bies-milestone-${i + 1}`);

      if (totalPrice >= tier.threshold) {
        unlockedCount = i + 1;
        if (milestone && !milestone.classList.contains('is-unlocked')) {
          milestone.classList.add('is-unlocked');
        }
      } else {
        if (!nextTier) nextTier = tier;
        if (milestone) milestone.classList.remove('is-unlocked');
      }
    }

    // Update dynamic text with compelling copy
    if (rewardsText) {
      if (unlockedCount === this.REWARDS_TIERS.length) {
        rewardsText.innerHTML = '<strong>ENVÍO GRATIS + 3 REGALOS ACUMULABLES</strong> DESBLOQUEADOS';
        rewardsSection.classList.add('is-complete');
      } else {
        rewardsSection.classList.remove('is-complete');
        const remaining = this.formatMoney(nextTier.threshold - totalPrice);

        if (unlockedCount === 0) {
          rewardsText.innerHTML = `TE FALTAN <strong>${remaining}</strong> PARA ENVÍO GRATIS + ${nextTier.giftName.toUpperCase()} GRATIS`;
        } else if (unlockedCount === 1) {
          rewardsText.innerHTML = `1 REGALO ACUMULADO · AÑADE <strong>${remaining}</strong> MÁS PARA ${nextTier.giftName.toUpperCase()}`;
        } else if (unlockedCount === 2) {
          rewardsText.innerHTML = `2 REGALOS ACUMULADOS · SOLO <strong>${remaining}</strong> MÁS PARA ${nextTier.giftName.toUpperCase()}`;
        }
      }
    }

    // Gift badges removed — progress bar milestones are self-explanatory

  }

  /**
   * BIES Smart Recommendations System
   * Uses Shopify Recommendations API + intelligent filtering
   */
  async updateRecommendedProducts(cart) {
    const recommendedContainer = document.getElementById('bies-cart-recommended-items');
    if (!recommendedContainer) {
      biesDebug('[BIES Smart Recs] Recommended container not found');
      return;
    }

    const recommendedSection = recommendedContainer.closest('.bies-cart-drawer__recommended');

    // Show loading state
    recommendedContainer.innerHTML = '<div style="text-align:center;padding:20px;color:#666;">Cargando recomendaciones...</div>';

    // Get cart product IDs and types
    const cartProductIds = cart.items.map(item => item.product_id);
    const cartProductTypes = await this.getCartProductTypes(cart.items);

    biesDebug('[BIES Smart Recs] Cart product IDs:', cartProductIds);
    biesDebug('[BIES Smart Recs] Cart product types:', cartProductTypes);

    try {
      let allRecommendations = [];

      // Step 1: Get recommendations from Shopify API for all cart items in parallel
      const recsArrays = await Promise.all(
        cart.items.map(item => this.fetchShopifyRecommendations(item.product_id))
      );
      allRecommendations = recsArrays.flat();

      biesDebug('[BIES Smart Recs] Raw recommendations from API:', allRecommendations.length);

      // Step 2: Deduplicate and filter recommendations
      const seen = new Set();
      const filteredRecommendations = allRecommendations.filter(product => {
        // Skip if already seen
        if (seen.has(product.id)) return false;
        seen.add(product.id);

        // Skip if already in cart
        if (cartProductIds.includes(product.id)) {
          biesDebug('[BIES Smart Recs] Excluding (in cart):', product.title);
          return false;
        }

        // Skip if not available
        if (!product.available) return false;

        // Skip hidden products (tags can be array or string)
        if (product.tags) {
          const tagsLower = Array.isArray(product.tags)
            ? product.tags.map(t => t.toLowerCase()).join(' ')
            : product.tags.toLowerCase();
          if (tagsLower.includes('hidden')) return false;
        }

        // Step 3: SMART FILTER - Exclude same product type
        const productType = this.inferProductType(product);
        if (cartProductTypes.includes(productType)) {
          biesDebug('[BIES Smart Recs] Excluding (same type "' + productType + '"):', product.title);
          return false;
        }

        // Skip packs if user has individual products of that type
        if (this.isPackContainingCartTypes(product, cartProductTypes)) {
          biesDebug('[BIES Smart Recs] Excluding (pack contains cart type):', product.title);
          return false;
        }

        return true;
      });

      biesDebug('[BIES Smart Recs] Filtered recommendations:', filteredRecommendations.length);

      // Step 4: If not enough recommendations, get fallback products
      let finalRecommendations = filteredRecommendations.slice(0, 3);

      if (finalRecommendations.length < 3) {
        biesDebug('[BIES Smart Recs] Not enough recs, fetching fallback...');
        const fallbackProducts = await this.getFallbackRecommendations(
          cartProductIds,
          cartProductTypes,
          finalRecommendations.map(p => p.id)
        );
        finalRecommendations = finalRecommendations.concat(fallbackProducts).slice(0, 3);
      }

      biesDebug('[BIES Smart Recs] Final recommendations:', finalRecommendations.map(p => p.title));

      // Step 5: Render recommendations
      if (finalRecommendations.length === 0) {
        if (recommendedSection) recommendedSection.style.display = 'none';
        return;
      }

      if (recommendedSection) recommendedSection.style.display = 'block';
      recommendedContainer.innerHTML = finalRecommendations.map(product => this.renderRecommendedProduct(product)).join('');

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'smart_recommendations_shown',
          products: finalRecommendations.map(p => p.title),
          cart_types: cartProductTypes
        });
      }

    } catch (error) {
      console.error('[BIES Smart Recs] Error:', error);
      // Fallback to simple recommendations
      await this.showFallbackRecommendations(cartProductIds, cartProductTypes, recommendedContainer, recommendedSection);
    }
  }

  /**
   * Get product types for all items in cart
   */
  async getCartProductTypes(items) {
    const results = await Promise.all(items.map(async (item) => {
      try {
        const response = await fetch(`/products/${item.handle}.js`);
        const product = await response.json();
        return this.inferProductType(product);
      } catch (e) {
        return this.inferTypeFromTitle(item.product_title);
      }
    }));
    return [...new Set(results.filter(Boolean))];
  }

  /**
   * Infer product type from product data or title
   */
  inferProductType(product) {
    // First, check if product_type is defined
    if (product.product_type && product.product_type.trim() !== '') {
      return product.product_type.toLowerCase();
    }

    // Otherwise, infer from title
    return this.inferTypeFromTitle(product.title);
  }

  /**
   * Infer product type from title using keywords
   */
  inferTypeFromTitle(title) {
    const titleLower = (title || '').toLowerCase();

    // Order matters - more specific matches first
    if (titleLower.includes('batido de proteína') || titleLower.includes('batido proteína') || titleLower.includes('proteina')) {
      return 'proteina';
    }
    if (titleLower.includes('pancakes') || titleLower.includes('tortitas')) {
      return 'pancakes';
    }
    if (titleLower.includes('granola')) {
      return 'granola';
    }
    if (titleLower.includes('café') || titleLower.includes('cafe')) {
      return 'cafe';
    }
    if (titleLower.includes('matcha')) {
      return 'matcha';
    }
    if (titleLower.includes('bebida instantánea') || titleLower.includes('bebida instantanea')) {
      return 'bebida';
    }
    if (titleLower.includes('shaker')) {
      return 'shaker';
    }
    if (titleLower.includes('endulzante') || titleLower.includes('stevia')) {
      return 'endulzante';
    }
    if (titleLower.includes('boost') || titleLower.includes('pack') || titleLower.includes('rutina') || titleLower.includes('desayuno')) {
      return 'pack';
    }

    return 'otro';
  }

  /**
   * Check if a pack contains products of types already in cart
   */
  isPackContainingCartTypes(product, cartTypes) {
    const titleLower = (product.title || '').toLowerCase();

    // Check if it's a pack
    if (!titleLower.includes('boost') && !titleLower.includes('pack') &&
        !titleLower.includes('rutina') && !titleLower.includes('desayuno')) {
      return false;
    }

    // Check if pack contains cart types
    for (const type of cartTypes) {
      if (type === 'proteina' && (titleLower.includes('proteína') || titleLower.includes('proteina'))) {
        return true;
      }
      if (type === 'cafe' && titleLower.includes('café')) {
        return true;
      }
      if (type === 'granola' && titleLower.includes('granola')) {
        return true;
      }
      if (type === 'shaker' && titleLower.includes('shaker')) {
        return true;
      }
      if (type === 'bebida' && titleLower.includes('bebida')) {
        return true;
      }
    }

    return false;
  }

  /**
   * Fetch recommendations from Shopify API
   */
  async fetchShopifyRecommendations(productId) {
    try {
      const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10&intent=related`);
      const data = await response.json();
      return data.products || [];
    } catch (error) {
      biesDebug('[BIES Smart Recs] API error for product', productId, error);
      return [];
    }
  }

  /**
   * Get fallback recommendations when API doesn't provide enough
   */
  async getFallbackRecommendations(excludeIds, excludeTypes, alreadyRecommendedIds) {
    try {
      const response = await fetch('/products.json?limit=30');
      const data = await response.json();

      const allExcludeIds = [...excludeIds, ...alreadyRecommendedIds];

      return (data.products || []).filter(product => {
        if (allExcludeIds.includes(product.id)) return false;
        if (!product.available) return false;
        if (product.tags && product.tags.toLowerCase().includes('hidden')) return false;

        const type = this.inferProductType(product);
        if (excludeTypes.includes(type)) return false;

        // Also exclude packs
        if (type === 'pack') return false;

        return true;
      }).slice(0, 3);
    } catch (error) {
      biesDebug('[BIES Smart Recs] Fallback error:', error);
      return [];
    }
  }

  /**
   * Show simple fallback recommendations on error
   */
  async showFallbackRecommendations(excludeIds, excludeTypes, container, section) {
    try {
      const products = await this.getFallbackRecommendations(excludeIds, excludeTypes, []);

      if (products.length === 0) {
        if (section) section.style.display = 'none';
        return;
      }

      if (section) section.style.display = 'block';
      container.innerHTML = products.slice(0, 3).map(product => this.renderRecommendedProduct(product)).join('');
    } catch (error) {
      console.error('[BIES Smart Recs] Fallback display error:', error);
      if (section) section.style.display = 'none';
    }
  }

  /**
   * Render a single recommended product
   */
  renderRecommendedProduct(product) {
    const variant = product.variants ? product.variants[0] : product;
    const image = product.images && product.images.length > 0
      ? product.images[0]
      : (product.featured_image || '');
    const price = variant.price || product.price;
    const variantId = variant.id || product.id;

    return `
      <div class="bies-cart-recommended-item">
        ${image ? `
          <img
            src="${typeof image === 'string' ? image : image.src}"
            alt="${this.escapeHtml(product.title)}"
            class="bies-cart-recommended-item__image"
            loading="lazy"
          >
        ` : ''}
        <div class="bies-cart-recommended-item__details">
          <h4 class="bies-cart-recommended-item__title">${this.escapeHtml(product.title)}</h4>
          <p class="bies-cart-recommended-item__price">${this.formatMoney(price)}</p>
        </div>
        <button
          class="bies-cart-recommended-item__add-btn"
          data-add-recommended
          data-variant-id="${variantId}"
        >
          Añadir
        </button>
      </div>
    `;
  }

  setLoading(isLoading) {
    if (!this.drawer) return;

    if (isLoading) {
      this.drawer.classList.add('is-loading');
    } else {
      this.drawer.classList.remove('is-loading');
    }
  }

  showNotification(message, type = 'success') {
    const notification = document.getElementById('bies-cart-notification');
    if (!notification) return;

    notification.textContent = message;
    notification.className = `bies-cart-drawer__notification is-visible ${type}`;

    setTimeout(() => {
      notification.classList.remove('is-visible');
    }, 3000);
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }

  parseMoney(text) {
    // Parse "36,98 €" or "36,98\u00a0€" back to cents (3698)
    const cleaned = text.replace(/[^\d,]/g, '').replace(',', '.');
    return Math.round(parseFloat(cleaned) * 100) || 0;
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }
}

// Initialize cart drawer
const biesCartDrawer = new BiesCartDrawer();

// Expose globally for external triggers
window.biesCartDrawer = biesCartDrawer;

// Listen for add-to-cart events from product forms
document.addEventListener('submit', async (e) => {
  const form = e.target;

  // Check if it's a product form - be more aggressive in detection
  const isProductForm = form.getAttribute('action') === '/cart/add' ||
                        form.getAttribute('action')?.includes('/cart/add') ||
                        form.classList.contains('product-form') ||
                        form.querySelector('[name="id"]'); // Has variant ID input

  if (isProductForm) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    biesDebug('[BIES Cart Drawer] Intercepted add-to-cart form submission');

    const formData = new FormData(form);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const data = await response.json();
      biesDebug('[BIES Cart Drawer] Product added:', data);

      // add.js doesn't return full cart, fetch + process
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      await biesCartDrawer.processCart(cart);
      biesCartDrawer.open({ skipUpdate: true });

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'add_to_cart',
          form_id: form.id || 'unknown'
        });
      }

    } catch (error) {
      console.error('[BIES Cart Drawer] Error adding to cart:', error);
      alert('Error al añadir el producto al carrito');
    }

    return false;
  }
}, true); // Use capture phase to intercept earlier

// Intercept cart icon clicks in header
document.addEventListener('click', (e) => {
  const cartIcon = e.target.closest('#cart-icon-bubble');
  if (cartIcon) {
    e.preventDefault();
    e.stopPropagation();
    biesDebug('[BIES Cart Drawer] Cart icon clicked, opening drawer');
    biesCartDrawer.open();
  }
}, true);

// Also intercept any link to /cart to open drawer instead
document.addEventListener('click', (e) => {
  const link = e.target.closest('a[href="/cart"]');
  if (link) {
    e.preventDefault();
    e.stopPropagation();
    biesDebug('[BIES Cart Drawer] Cart link clicked, opening drawer');
    biesCartDrawer.open();
  }
}, true);

biesDebug('[BIES Cart Drawer] Initialized successfully');
